import {
  countDecisions,
  type ImportColumnDecision,
  type ImportItemType,
  type ImportMappingIssue,
  validateMapping,
} from "@lafayette/import-mapping";
import type { StaffImportDecisionInput } from "@lafayette/schemas";

// The decision reducer of the mapping screen. The state is one decision per source column, in column
// order. The functions are pure. The rules they keep:
//   - a suggestion that is not HIGH is shown and NEVER applied: `accept` is the only way to apply it;
//   - a decision is `origin: SUGGESTION` only while it equals what the suggester produced, and
//     `STAFF` otherwise (the same rule the server applies when it saves the mapping);
//   - `remember` is kept only for a mapped STAFF decision.

export type ColumnDecisions = readonly ImportColumnDecision[];

/** What the suggester produced for the column: a HIGH match is applied, anything else is not. */
function suggestedDecision(decision: ImportColumnDecision): {
  decision: ImportColumnDecision["decision"];
  targetField: string | null;
} {
  return decision.confidence === "HIGH" && decision.suggestedTargetField !== null
    ? { decision: "MAPPED", targetField: decision.suggestedTargetField }
    : { decision: "UNMAPPED", targetField: null };
}

function change(
  decisions: ColumnDecisions,
  columnIndex: number,
  next: Readonly<{
    decision: ImportColumnDecision["decision"];
    targetField: string | null;
  }>,
): ColumnDecisions {
  return decisions.map((current) => {
    if (current.columnIndex !== columnIndex) return current;
    const suggested = suggestedDecision(current);
    const untouched =
      next.decision === suggested.decision && next.targetField === suggested.targetField;
    return {
      ...current,
      decision: next.decision,
      targetField: next.targetField,
      origin: untouched ? "SUGGESTION" : "STAFF",
      remember: untouched || next.decision !== "MAPPED" ? false : current.remember,
    };
  });
}

/** Staff choose a target field for a column. */
export function mapColumn(
  decisions: ColumnDecisions,
  columnIndex: number,
  targetField: string,
): ColumnDecisions {
  return change(decisions, columnIndex, { decision: "MAPPED", targetField });
}

/** Staff accept the "Did you mean" suggestion of a column. It does nothing without a suggestion. */
export function acceptSuggestion(decisions: ColumnDecisions, columnIndex: number): ColumnDecisions {
  const column = decisions.find((decision) => decision.columnIndex === columnIndex);
  if (column?.suggestedTargetField == null) return decisions;
  return mapColumn(decisions, columnIndex, column.suggestedTargetField);
}

export function ignoreColumn(decisions: ColumnDecisions, columnIndex: number): ColumnDecisions {
  return change(decisions, columnIndex, { decision: "IGNORED", targetField: null });
}

/** Back to "not decided yet". */
export function unmapColumn(decisions: ColumnDecisions, columnIndex: number): ColumnDecisions {
  return change(decisions, columnIndex, { decision: "UNMAPPED", targetField: null });
}

export function setRemember(
  decisions: ColumnDecisions,
  columnIndex: number,
  remember: boolean,
): ColumnDecisions {
  return decisions.map((current) =>
    current.columnIndex === columnIndex &&
    current.decision === "MAPPED" &&
    current.origin === "STAFF"
      ? { ...current, remember }
      : current,
  );
}

/** Whether the "Remember for next imports" box is offered: only for a column that staff mapped. */
export function canRemember(decision: ImportColumnDecision): boolean {
  return decision.decision === "MAPPED" && decision.origin === "STAFF";
}

/** The intent only, as the request schema wants it. The server works out the rest. */
export function toDecisionInputs(decisions: ColumnDecisions): StaffImportDecisionInput[] {
  return decisions.map((decision) =>
    decision.decision === "MAPPED"
      ? {
          columnIndex: decision.columnIndex,
          decision: "MAPPED",
          targetField: decision.targetField,
          ...(canRemember(decision) && decision.remember ? { remember: true } : {}),
        }
      : { columnIndex: decision.columnIndex, decision: decision.decision },
  );
}

/** What differs between two states is what staff chose: compare the intents. */
export function sameMapping(
  a: Readonly<{ decisions: ColumnDecisions; defaultItemType: ImportItemType | null }>,
  b: Readonly<{ decisions: ColumnDecisions; defaultItemType: ImportItemType | null }>,
): boolean {
  return (
    a.defaultItemType === b.defaultItemType &&
    JSON.stringify(toDecisionInputs(a.decisions)) === JSON.stringify(toDecisionInputs(b.decisions))
  );
}

export const isItemTypeMapped = (decisions: ColumnDecisions): boolean =>
  decisions.some((d) => d.decision === "MAPPED" && d.targetField === "item_type");

/** Mapping a column to Item type replaces the job's own item type. */
export function withoutDefaultItemTypeConflict(
  decisions: ColumnDecisions,
  defaultItemType: ImportItemType | null,
): ImportItemType | null {
  return isItemTypeMapped(decisions) ? null : defaultItemType;
}

export function mappingProblems(
  decisions: ColumnDecisions,
  defaultItemType: ImportItemType | null,
  stage: "draft" | "submit",
): readonly ImportMappingIssue[] {
  return validateMapping({
    columnCount: decisions.length,
    decisions: toDecisionInputs(decisions),
    defaultItemType,
    stage,
  });
}

/** The first problem of each column (a column can have several; one sentence is enough under its row). */
export function problemsByColumn(
  issues: readonly ImportMappingIssue[],
): ReadonlyMap<number, ImportMappingIssue> {
  const byColumn = new Map<number, ImportMappingIssue>();
  for (const issue of issues) {
    if (issue.columnIndex !== null && !byColumn.has(issue.columnIndex)) {
      byColumn.set(issue.columnIndex, issue);
    }
  }
  return byColumn;
}

/** "12 mapped, 2 ignored, 1 not mapped" */
export function describeCounts(decisions: ColumnDecisions): string {
  const { mapped, ignored, unmapped } = countDecisions(decisions);
  return `${mapped} mapped, ${ignored} ignored, ${unmapped} not mapped`;
}
